/**
 * Widget registry — schema-only export.
 *
 * Imported by:
 *   - apps/agent (validates widget directive props before emitting via
 *     emit-widget-directive's 4-layer validation; spec §7.6)
 *   - apps/web (validates widget directive props before mounting via
 *     MarkdownRenderer)
 *   - packages/tooling/lint-cells (build-time validation that every
 *     :::widget[X]:::: directive in content/ + prompts/ resolves)
 *
 * MUST NOT import React. Each widget's React component lives in
 * apps/web/src/components/widgets/{name}.tsx.
 *
 * Adding a widget: add a 4-line defineWidget() entry to schemas.ts.
 * No new file or folder needed.
 */
import type { z } from 'zod';

export type WidgetMeta<TProps = unknown> = {
  /** Widget name as referenced in role.yaml#widgets and :::widget[X]::: directives. */
  name: string;
  /** Zod schema for this widget's props; agent and web both call .parse(). */
  schema: z.ZodType<TProps>;
  /** Short human-readable description (shown in agent's tool catalog + lint output). */
  description: string;
  /** Roles whose role.yaml#widgets MAY include this widget. */
  rolesAllowedToEmit: ReadonlyArray<string>;
};

import { WIDGET_SCHEMAS, WIDGET_BY_NAME, type WidgetName } from './schemas';

export const widgetRegistry = WIDGET_BY_NAME;

export type { WidgetName };

export { WIDGET_SCHEMAS, WIDGET_BY_NAME };
